import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { useFetch } from 'hooks';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { AppLayout } from 'layouts';
import { Icon, Loading, SkeletonText, Typography } from 'components/atoms';
import { Breadcrumbs, SectionHeading, Tabs } from 'components/molecules';
import {
  Audit,
  DueDiligence,
  OutstandingActions,
  Profile,
  LinkedThirdparties,
  ScreeningRisks,
  Documents,
  DueDiligenceDrafts,
} from './sections';
import { Column, InitiateButton, Layout, Section, TabContent } from './ThirdPartyProfile.styles';
import DueDiligenceRiskModule from 'components/organisms/duediligence-risk-module';
import { GatewaySubscriptionTypes } from 'auth/constants/subscriptions';
import { GatewayPermissions } from 'auth/constants/permissions';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import { ThirdPartyType, ThirdPartyModelResponse, LinkedThirdPartyFieldsConfigs } from 'types';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import { getThirdPartyConfiguration } from 'services/getThirdPartyConfiguration';
import { BreadcrumbItem } from 'components/molecules/breadcrumbs/types';
import { ThirdPartyRouteInfo } from 'types/router';

const ThirdPartyProfile = () => {
  const { tokenInfo } = useAuth();
  const { hasPermission, hasSubscription } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const location = useLocation<{
    mainThirdParty: ThirdPartyRouteInfo;
    linkedThirdParty: ThirdPartyRouteInfo;
  }>();
  const locationState = location.state;
  const { msalSubscriptionKey, xFunctionsKey, vantageWebApi } = useConfig();
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, responseStatus } = useFetch<ThirdPartyModelResponse>(
    `${vantageWebApi}/thirdParty/${id}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );
  const [selected, setSelected] = useState(0);
  const [thirdPartyDataConfig, setThirdPartyDataConfig] = useState(null);

  const history = useHistory();

  const headers = useMemo(
    () => ({
      'Ocp-Apim-Subscription-Key': msalSubscriptionKey,
      'X-functions-Key': xFunctionsKey,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    }),
    [msalSubscriptionKey, xFunctionsKey, tokenInfo.accessToken]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }

    const { type } = data;
    getThirdPartyConfiguration(vantageWebApi, headers, type).then((config) =>
      setThirdPartyDataConfig(config)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, vantageWebApi, headers]);

  const getBreadcrumb = (): BreadcrumbItem[] => {
    if (locationState?.linkedThirdParty) {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: locationState.mainThirdParty.name,
          route: `/third-party/profile/${locationState.mainThirdParty.id}`,
        },
        {
          label: locationState.linkedThirdParty.name,
        },
      ] as BreadcrumbItem[];
    } else {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: data?.name,
        },
      ] as BreadcrumbItem[];
    }
  };

  const isEntityTypeUnknown = data?.type === ThirdPartyType.Unknown;

  const canViewDocuments: boolean =
    hasPermission(GatewayPermissions.ViewDocuments) && !isEntityTypeUnknown;

  const canViewDrafts = () => {
    return data.draftsCount > 0;
  };

  const canViewScreeningRisks: boolean =
    hasSubscription(GatewaySubscriptionTypes.ManagedServiceScreening) ||
    hasSubscription(GatewaySubscriptionTypes.SelfServiceScreening);

  const canViewDueDiligenceRiskModule: boolean =
    hasSubscription(GatewaySubscriptionTypes.ManagedServiceDueDiligence) &&
    hasPermission(GatewayPermissions.ViewDueDiligenceReport) &&
    !isEntityTypeUnknown;

  const canInitiateDueDiligence = !(
    hasPermission(GatewayPermissions.InitiateDueDiligenceSelfServiceScreening) ||
    hasPermission(GatewayPermissions.InitiateDueDiligenceManagedServiceScreening) ||
    hasPermission(GatewayPermissions.OrderManagedDueDiligence)
  );

  const canViewAudits: boolean = hasPermission(GatewayPermissions.ViewAudit);

  const canViewTaskList: boolean = hasPermission(GatewayPermissions.ViewTaskList);

  const thirdPartyInfo = data
    ? {
        id: data.internalId,
        name: data.name,
        thirdpartyId: id,
        type: data.type,
      }
    : null;

  const linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs = {
    isIndividualRelationshipTypeMandatory: data?.individualRelationshipType,
    isIndividualOwnershipPercentageMandatory: data?.individualOwnershipPercentage,
    isIndividualOwnershipPercentageEnabled: data?.individualOwnershipPercentageEnabled,
    isCompanyRelationshipTypeMandatory: data?.companyRelationshipType,
    isCompanyOwnershipPercentageMandatory: data?.companyOwnershipPercentage,
    isCompanyOwnershipPercentageEnabled: data?.companyOwnershipPercentageEnabled,
    isUnknownRelationshipTypeMandatory: data?.unknownRelationshipType,
    isUnknownOwnershipPercentageMandatory: data?.unknownOwnershipPercentage,
    isUnknownOwnershipPercentageEnabled: data?.unknownOwnershipPercentageEnabled,
  };

  const breadcrumbComp = loading ? <SkeletonText /> : <Breadcrumbs items={getBreadcrumb()} />;
  if (error) {
    if (responseStatus === 404) {
      return <Redirect to="/not-found" />;
    }
    if (responseStatus === 401) {
      return <Redirect to="/unauthorised" />;
    }
    return (
      <AppLayout topBarSlot={breadcrumbComp}>
        <Section large>
          <SectionHeading text="Error" />
          <Typography tag="p" value="Oops something went wrong!" />
        </Section>
      </AppLayout>
    );
  }

  if (loading || error) {
    return (
      <AppLayout topBarSlot={breadcrumbComp}>
        <Section large>
          {loading ? (
            <Loading />
          ) : (
            <>
              <SectionHeading text="Error" />
              <Typography tag="p" value="There was an error with the request." />
            </>
          )}
        </Section>
      </AppLayout>
    );
  }

  return (
    <AppLayout topBarSlot={breadcrumbComp}>
      {data && (
        <>
          <Layout>
            <Column>
              <Profile
                id={id}
                profile={data}
                thirdPartyDataConfig={thirdPartyDataConfig}
                routeState={locationState}
              />
            </Column>
            <Column>
              {canViewTaskList && <OutstandingActions />}

              <Tabs
                dark
                selected={selected}
                setSelected={setSelected}
                ctas={[
                  {
                    i: [0, 1, 2],
                    cta: (
                      <InitiateButton
                        secondary
                        disabled={canInitiateDueDiligence}
                        onClick={() => history.push(`/initiate-due-diligence/${id}`)}
                      >
                        <span>
                          <Icon icon="report" size="xs" colour="inherit" />
                          <span>Initiate compliance checks</span>
                        </span>
                      </InitiateButton>
                    ),
                  },
                ]}
              >
                <TabContent label="Risk summary">
                  <Section large>
                    <DueDiligence />
                  </Section>
                  {canViewScreeningRisks && (
                    <Section large>
                      <ScreeningRisks {...data?.screeningRequestRiskModel} type={data?.type} />
                    </Section>
                  )}
                  {canViewDueDiligenceRiskModule && (
                    <Section large>
                      <DueDiligenceRiskModule
                        riskRating={data?.dueDiligenceRequestRiskModel}
                        riskRatingMethodology={data?.riskRatingMethodologyId}
                      />
                    </Section>
                  )}
                </TabContent>
                {canViewDocuments && (
                  <TabContent label={`${terminologyConfig?.thirdPartySingularNaming} documents`}>
                    <Section large>
                      <Documents thirdPartyId={id} type={data?.type} />
                    </Section>
                  </TabContent>
                )}
                {canViewDrafts() && (
                  <TabContent label={'Drafts Summary (' + data?.draftsCount + ')'}>
                    <Section large>
                      <DueDiligenceDrafts thirdPartyId={id} draftsCount={data?.draftsCount} />
                    </Section>
                  </TabContent>
                )}
              </Tabs>
            </Column>
          </Layout>
          <LinkedThirdparties
            thirdParty={thirdPartyInfo}
            numberOfLinkedThirdParties={data.linkedThirdPartiesCount}
            linkedThirdPartyConfigurations={linkedThirdPartyConfigurations}
          />
          {canViewAudits && <Audit />}
        </>
      )}
    </AppLayout>
  );
};

export default withAITracking(reactPlugin, ThirdPartyProfile);
